import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoadWithInput } from "@redotech/react-util/load";
import { BrandKit } from "@redotech/redo-model/brand-kit";
import {
  ShortAnswerData,
  SystemSemanticTagId,
} from "@redotech/redo-model/forge/review-forge-types";
import { ReviewStatus } from "@redotech/redo-model/review/review";
import { ReviewBody } from "@redotech/redo-model/review/review-body-schema";
import { REDO_REVIEW_HASH } from "@redotech/redo-model/storefront/navigation";
import {
  ButtonSize,
  RedoButtonCloseX,
} from "@redotech/redo-web/arbiter-components/redo-button-close-x";
import { Flex } from "@redotech/redo-web/flex";
import { UploaderContext } from "@redotech/redo-web/image-upload";
import { ReviewForm } from "@redotech/redo-web/reviews-form/review-form";
import {
  BrandKitContext,
  DiscountContext,
  ProductBeingReviewedContext,
} from "@redotech/redo-web/reviews-form/review-form-editor-contexts";
import {
  PossibleSubmittableData,
  ReviewsFormValue,
} from "@redotech/redo-web/reviews-form/review-form-types";
import { MerchantCustomizableModal } from "@redotech/redo-web/storefront/merchant-customizable-modal";
import { Text } from "@redotech/redo-web/text";
import { createReviewDiscount } from "@redotech/shopify-sdk/rpc/schema/create-review-discount";
import { handleReviewEvent } from "@redotech/shopify-sdk/rpc/schema/handle-review-event";
import { upsertReview } from "@redotech/shopify-sdk/rpc/schema/upsert-review";
import { memo, useEffect, useMemo, useState } from "react";
import { ShopifyExtensionClientContext } from "../../client/client-context";
import { RedoShopifyRpcClientContext } from "../../client/redo-shopify-rpc-client";
import { uploadFile } from "../../client/shopify-extension-client";
import { formDataToReviewField } from "../../utils/form-data-to-review-field";
import { reviewToFormData } from "../../utils/review-to-form-data";
import * as styles from "./reviews-survey-modal.module.css";

export const ReviewsSurveyModal = memo(function ReviewsSurveyModal({
  reviewsFormValue,
  customerEmail,
  productId,
  productInfo,
  orderInfo,
  brandKit,
  existingReview,
  reviewId,
}: {
  reviewsFormValue: ReviewsFormValue;
  customerEmail: string | null;
  productId: string | null;
  productInfo: { title: string; imageSrc?: string | undefined } | undefined;
  orderInfo?: { id: string; orderDate: Date } | undefined;
  brandKit: BrandKit;
  existingReview?: ReviewBody | null;
  reviewId?: string | null;
}) {
  const [open, setOpen] = useState(false);

  const [title, imageSrc] = productInfo
    ? [productInfo.title, productInfo.imageSrc]
    : [undefined, undefined];

  const openModalIfRedoReviewsHash = () => {
    if (location.hash === REDO_REVIEW_HASH) {
      setOpen(true);
    }
  };

  useEffect(() => {
    // Open modal if there's a review hash or if we're editing a review
    if (location.hash === REDO_REVIEW_HASH || reviewId) {
      setOpen(true);
    }

    addEventListener("hashchange", openModalIfRedoReviewsHash);

    return () => {
      removeEventListener("hashchange", openModalIfRedoReviewsHash);
    };
  }, [reviewId]);

  const rpcClient = useRequiredContext(RedoShopifyRpcClientContext);

  const handleClose = useHandler(() => {
    if (location.hash.includes(REDO_REVIEW_HASH)) {
      const newUrl = new URL(location.href);
      newUrl.hash = "";
      history.replaceState({}, "", newUrl);
    }
    setOpen(false);
  });

  const [submitTriggerAction, triggerSubmit] = useTriggerLoadWithInput(
    async (
      formData: Record<string, PossibleSubmittableData>,
    ): Promise<{
      discountCode: string;
      formattedDiscountString: string;
    } | null> => {
      if (!productId) {
        console.error("Product ID is required to submit a review");
        throw new Error("Product ID is required to submit a review");
      }

      const emailField = Object.entries(formData).find(
        ([key]) => key === SystemSemanticTagId.REVIEW_EMAIL,
      );
      const reviewEmail = emailField
        ? (emailField[1] as ShortAnswerData).answer
        : customerEmail;

      const { reviewId: returnedReviewId } = await rpcClient(upsertReview, {
        status: ReviewStatus.PENDING,
        shopifyProductId: productId,
        customer: reviewEmail ? { email: reviewEmail } : undefined,
        reviewFields: Object.entries(formData).map((value) =>
          formDataToReviewField(value),
        ),
        existingReviewId: reviewId || undefined,
        orderId: orderInfo?.id || undefined,
      });
      // Only create a discount for new reviews, not edits
      let discountResults = null;
      if (!reviewId) {
        discountResults = await rpcClient(createReviewDiscount, {
          reviewId: returnedReviewId,
        });
      }

      if (reviewEmail) {
        await rpcClient(handleReviewEvent, {
          email: reviewEmail,
          discountCode: discountResults?.discountCode ?? undefined,
        });
      }

      return discountResults;
    },
  );

  const submitAction = {
    triggerSubmit: (formData: Record<string, PossibleSubmittableData>) =>
      triggerSubmit(formData),
    state: {
      ...submitTriggerAction,
      value:
        submitTriggerAction.value !== undefined ||
        submitTriggerAction.error !== undefined
          ? ("success" as const)
          : undefined,
    },
  };

  const shopifyExtensionClient = useRequiredContext(
    ShopifyExtensionClientContext,
  );

  // Convert existing review to form data if editing
  const initialFormData = useMemo(() => {
    if (existingReview) {
      return reviewToFormData(existingReview);
    }
    return undefined;
  }, [existingReview]);

  return (
    <MerchantCustomizableModal
      fullscreen={false}
      onClose={handleClose}
      open={open}
    >
      <UploaderContext.Provider
        value={async ({ file, signal }) => {
          const form = new FormData();
          form.append("file", file);
          form.append("fileName", file.name);

          const response = await uploadFile(shopifyExtensionClient, form, {
            signal,
          });

          if (response.success) {
            return { url: new URL(response.body.url) };
          } else {
            throw new Error(response.error);
          }
        }}
      >
        <BrandKitContext.Provider value={brandKit}>
          <ProductBeingReviewedContext.Provider
            value={{
              imageUrl: imageSrc ?? "",
              title: title ?? "",
              purchaseDate: orderInfo?.orderDate,
            }}
          >
            <DiscountContext.Provider value={submitTriggerAction.value ?? null}>
              {reviewsFormValue && (
                <Flex
                  dir="column"
                  gap="xl"
                  style={{
                    width: "800px",
                    minWidth: "auto",
                    maxWidth: "calc(100vw - 32px)",
                    maxHeight: "90vh",
                    overflowX: "hidden",
                  }}
                >
                  <Flex
                    className={styles.closeButton}
                    style={{ height: "0px" }}
                  >
                    <RedoButtonCloseX
                      onclick={handleClose}
                      onDarkBackground={false}
                      size={ButtonSize.SMALL}
                    />
                  </Flex>
                  {reviewId && existingReview && (
                    <Flex
                      align="center"
                      bgColor="secondary"
                      justify="center"
                      p="lg"
                      style={{ backgroundColor: "#f0f8ff" }}
                    >
                      <Text color="primary" fontSize="sm" fontWeight="medium">
                        You are currently editing your existing review
                      </Text>
                    </Flex>
                  )}
                  <ReviewForm
                    form={reviewsFormValue}
                    handleClose={handleClose}
                    initialFormData={initialFormData}
                    submitAction={submitAction}
                  />
                </Flex>
              )}
            </DiscountContext.Provider>
          </ProductBeingReviewedContext.Provider>
        </BrandKitContext.Provider>
      </UploaderContext.Provider>
    </MerchantCustomizableModal>
  );
});
